/**
 * Reply Modal Component
 * NIP-10 compliant reply form
 */

import { useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, MessageCircle, Send, Loader2 } from 'lucide-react'
import { createReply } from '../../services/replyService'
import { useAuth } from '../../contexts/AuthContext'
import { EntryToolbar } from '../Entry/EntryToolbar'
import { Event } from 'nostr-tools'

interface ReplyModalProps {
  isOpen: boolean
  onClose: () => void
  replyToEvent: Event
  rootEvent?: Event
  authorName: string
  onSuccess?: () => void
}

export function ReplyModal({ 
  isOpen, 
  onClose, 
  replyToEvent, 
  rootEvent,
  authorName,
  onSuccess 
}: ReplyModalProps) {
  const { sk } = useAuth()
  const [content, setContent] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const handleSubmit = async () => {
    if (!content.trim()) {
      setError('Yanıt boş olamaz.')
      return
    }

    if (!sk && !window.nostr) {
      setError('Yanıt göndermek için giriş yapmalısınız.')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const result = await createReply({
        content: content.trim(),
        replyToEvent,
        rootEvent,
        sk
      })

      if (result.success) {
        setContent('')
        onSuccess?.()
        onClose()
      } else {
        setError(result.error || 'Yanıt gönderilemedi.')
      }
    } catch (err) {
      console.error('Reply error:', err)
      setError(err instanceof Error ? err.message : 'Yanıt gönderilemedi.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  // Preview of what's being replied to
  const previewContent = replyToEvent.content.length > 150 
    ? replyToEvent.content.slice(0, 150) + '...'
    : replyToEvent.content

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose()
        }}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white dark:bg-surface-900 rounded-2xl w-full max-w-lg shadow-xl overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-surface-200 dark:border-surface-800">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-mavi-500" />
              <h3 className="font-poppins font-semibold text-lg text-gray-900 dark:text-white">
                yanıtla
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Reply preview */}
          <div className="px-4 pt-4">
            <div className="bg-surface-50 dark:bg-surface-800/50 rounded-lg p-3 border-l-4 border-mavi-500">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                <span className="font-medium text-mavi-600 dark:text-mavi-400">
                  @{authorName}
                </span>
                {' '}adlı kullanıcıya yanıt
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-3">
                {previewContent}
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="p-4">
            <EntryToolbar
              textareaRef={textareaRef as React.RefObject<HTMLTextAreaElement>}
              value={content}
              onChange={setContent}
            />
            
            <textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="yanıtınızı yazın..."
              className="w-full h-32 p-3 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 resize-none focus:outline-none focus:ring-2 focus:ring-mavi-500/30 font-inter text-sm text-gray-900 dark:text-white placeholder:text-gray-400"
            />

            {error && (
              <p className="mt-2 text-sm text-red-500">{error}</p>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-lg transition-colors"
              >
                vazgeç
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !content.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-mavi-600 hover:bg-mavi-700 disabled:bg-mavi-600/50 text-white font-medium text-sm rounded-lg transition-colors"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    gönderiliyor...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    gönder
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
